import { useEffect, useState } from "react";
import { parse } from "svgson";
import { sanitizeSvg } from "../SvgEditor/helpers/importSvg";
import { splitSvgLayers, joinSvgLayers, extractLayerColors } from "../SvgEditor/helpers/svgLayers";
import { buildStitchElements, DEFAULT_STITCH_SETTINGS } from "./stitchRender";

/**
 * A community design drawn as thread rather than flat vector.
 *
 * The stored preview image is a server-side rasterisation of the plain SVG, so
 * it shows the artwork but not the embroidery — and the embroidery is the part
 * someone browsing the gallery is actually judging. This runs the same renderer
 * the editor canvas uses, against the creator's published per-layer settings.
 *
 * Read-only and inert by design: no editor state, no writes, nothing to drag.
 *
 * Rendering is deferred rather than computed during render. Satin columns are
 * measured by sampling real paths through the DOM, which is fast but
 * synchronous, and doing it inline would stall the modal's open animation. The
 * caller keeps showing the flat image until `onReady` fires, which also means a
 * design with no usable stitch state simply never swaps and degrades to the
 * picture it would have shown anyway.
 */

const readViewBox = (node) => {
  const raw = node?.attributes?.viewBox;
  if (raw) {
    const parts = raw.split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
      return { vbX: parts[0], vbY: parts[1], vbW: parts[2], vbH: parts[3] };
    }
  }
  const w = parseFloat(node?.attributes?.width);
  const h = parseFloat(node?.attributes?.height);
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { vbX: 0, vbY: 0, vbW: w, vbH: h };
  }
  return null;
};

// Mirrors the editor's base artwork pass: one compound path per layer so
// evenodd shapes keep their holes, sitting under the stitches as the fabric
// coverage they're sewn onto.
const buildArtworkElements = (joined, colors) =>
  (joined?.children || []).map((layer, i) => {
    const attrs = layer?.attributes || {};
    const d = attrs.d;
    if (!d) return null;
    const color = colors?.[i] || "#888888";
    return (
      <path
        key={`art-${i}`}
        d={d}
        fill={color}
        stroke={color}
        strokeWidth="1"
        strokeLinejoin="miter"
        fillRule={attrs["fill-rule"] || attrs.fillRule || "evenodd"}
      />
    );
  });

/**
 * @param {object} props
 * @param {string} props.svgUrl Public artwork to render.
 * @param {object} props.stitchState Published per-layer settings and hoop size.
 * @param {number} [props.intrinsicWidth] Natural size of the flat image this
 *   replaces, in pixels. Given both, the stitch render takes the exact box the
 *   browser gave that image, so swapping one for the other doesn't resize
 *   anything. Without them it falls back to the viewBox.
 * @param {number} [props.intrinsicHeight]
 * @param {Function} [props.onReady] Fired once, when there is something to show.
 */
const StitchPreview = ({
  svgUrl,
  stitchState,
  intrinsicWidth,
  intrinsicHeight,
  onReady,
  className,
  style,
}) => {
  const [artwork, setArtwork] = useState(null);
  const [render, setRender] = useState(null);

  // Fetching is kept separate from stitching so it can run alongside the
  // caller's Firestore read for the stitch state rather than queue behind it.
  useEffect(() => {
    if (!svgUrl) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(svgUrl);
        if (!response.ok) throw new Error(`Could not fetch design (${response.status})`);
        const parsed = await parse(sanitizeSvg(await response.text()));
        if (cancelled) return;

        const split = splitSvgLayers(parsed);
        const joined = joinSvgLayers(split);
        const viewBox = readViewBox(joined);
        if (!viewBox) throw new Error("Design has no usable viewBox");

        setArtwork({ split, joined, colors: extractLayerColors(joined), viewBox });
      } catch (error) {
        // Nothing to do: the caller is still showing the flat image.
        console.warn("Stitch preview unavailable:", error);
      }
    })();

    return () => { cancelled = true; };
  }, [svgUrl]);

  useEffect(() => {
    if (!artwork || !stitchState) return undefined;

    let cancelled = false;
    // One frame of breathing room so the modal finishes opening before the
    // satin sampling below takes the main thread.
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      const { split, joined, colors, viewBox } = artwork;

      const stitches = buildStitchElements({
        svgJoined: joined,
        svgSplit: split,
        stitchSettings: DEFAULT_STITCH_SETTINGS,
        colors,
        layerRails: stitchState.layerRails || {},
        viewBox,
        // The size the creator exported at. Stitch density is specified in
        // millimetres, so without this the spacing would be read against a
        // default size and the texture would come out too coarse or too fine.
        targetWidth: stitchState.hoopSettings?.targetWidth,
        embroidery: {
          getLayerSettings: (index) => stitchState.layerStitchSettings?.[index],
        },
        keyPrefix: "preview-stitch",
      });

      if (cancelled || !stitches) return;
      setRender({ viewBox, artwork: buildArtworkElements(joined, colors), stitches });
      onReady?.();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
    // onReady is a render-scoped callback; re-running on its identity would
    // re-sample the design on every parent render.
  }, [artwork, stitchState]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!render) return null;

  const { viewBox, artwork: artworkElements, stitches } = render;
  const boxW = intrinsicWidth || viewBox.vbW;
  const boxH = intrinsicHeight || viewBox.vbH;

  return (
    <svg
      className={className}
      style={{
        // The same rules the browser applies to an <img>: sit at natural size,
        // shrink to fit if the container is smaller, never distort. Width is
        // set explicitly rather than left auto because a CSS `width: auto` on
        // an SVG discards the element's own dimensions instead of falling back
        // to them, and it would balloon to fill the container.
        width: `${boxW}px`,
        height: "auto",
        aspectRatio: `${boxW} / ${boxH}`,
        maxWidth: "100%",
        maxHeight: "100%",
        ...style,
      }}
      viewBox={`${viewBox.vbX} ${viewBox.vbY} ${viewBox.vbW} ${viewBox.vbH}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Stitch preview"
    >
      {artworkElements}
      {stitches}
    </svg>
  );
};

export default StitchPreview;
